import { Http } from '@mygpt/app/http';

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };
export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}
export interface Versioned<T> {
  revision: number;
  value: T;
  replayed?: boolean;
  bound_origin?: string | null;
}
export interface HomepageTemplate {
  id: string;
  name: string;
  code: string;
  version: number;
  digest: string;
  enabled: boolean;
  created_at: string;
  asset_path: string;
  thumbnail_path?: string | null;
}
export interface TemplateFamily {
  code: string;
  latest: HomepageTemplate;
  version_count: number;
}
export interface Binding {
  template_id: string | null;
  published: boolean;
  draft_template_id?: string | null;
  page_name?: string;
  external_url?: string | null;
}
export type PortalCommand =
  | { action: 'stage'; template_id: string }
  | { action: 'publish' | 'discard' | 'unpublish' | 'unbind' }
  | { action: 'properties'; page_name: string; external_url: string | null }
  | { action: 'restore'; restore_revision: number };
export interface Homepage extends Versioned<Binding> {
  template: HomepageTemplate | null;
  draft_template?: HomepageTemplate | null;
  lifecycle_version?: number;
  public_url: string | null;
  can_manage_templates: boolean;
}
export interface AccessPolicy {
  enabled: boolean;
  full_stop: boolean;
  issuance_enabled: boolean;
  allocation_enabled: boolean;
  generation_enabled: boolean;
  event_collection_enabled: boolean;
  quota_scope: string;
  active_from: string | null;
  active_until: string | null;
  no_expiry?: boolean;
  session_ttl: number;
  allow_all_origins?: boolean;
  allowed_origins: string[];
  limits: {
    bot_requests_per_minute: number;
    visitor_requests_per_minute: number;
    sessions_per_ip_per_minute: number;
    daily_results: number;
    activity_results: number;
    daily_generations: number;
    event_bytes_per_minute: number;
  };
}
export interface BusinessRecord {
  id: string;
  bot_id: string;
  data_type: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  data?: JsonValue;
}
export interface EventRecord {
  id: string;
  bot_id: string;
  created_at: string;
  payload: JsonValue;
}
export interface Metric {
  key: string;
  confirmed: number;
  reserved: number;
  opportunities: number;
}
export interface UsageText {
  text: string;
  source: 'baseline' | 'generation';
  reported_at?: string | null;
  degraded: boolean;
}
export interface UsageRecord {
  id: string;
  created_at: string;
  data_version: string;
  mode: string | null;
  mode_label: string | null;
  type_label: string | null;
  answers: { question: string; choice: string; feedback: string | null }[];
  recommendations: {
    resource_id: string;
    group_id: string;
    shop?: string | null;
    product?: string | null;
    is_mock?: boolean | null;
    state: 'confirmed' | 'pending' | 'released' | 'expired';
  }[];
  baseline: string | null;
  presentation: UsageText | null;
  generated: UsageText | null;
  details_recorded: boolean;
}
export interface UsageDetail extends UsageRecord {
  additional: UsageRecord[];
  additional_truncated: boolean;
}
export interface UsageFilter {
  from?: string;
  to?: string;
  mode?: string;
}
export interface UsageInsights {
  from: string;
  to: string;
  observed_at: string;
  timezone: 'Asia/Tokyo';
  unit: 'diagnosis';
  mode: string | null;
  summary: {
    completed: number;
    displayed: number;
    clicked: number;
    ai_shown: number;
    reference_shown: number;
    missing_details: number;
    unconfirmed_clicks: number;
  };
  daily: { day: string; completed: number; displayed: number; clicked: number }[];
  shops: { shop_id: string; shop: string | null; recommended: number; displayed: number; clicked: number }[];
  shop_count: number;
}
export interface Bootstrap {
  bot_id: string;
  revision: number;
  template: HomepageTemplate;
  preview: boolean;
  bridge_version: number;
}
export interface History {
  id: string;
  revision: number;
  value: JsonValue;
  actor_email: string | null;
  reason: string;
  created_at: string;
}

const path = (botId: string, suffix: string) => `/robots/${encodeURIComponent(botId)}/${suffix}`;
export const homepageApi = {
  usageInsights: (botId: string, filter: UsageFilter) =>
    Http.use().get<UsageInsights>(path(botId, 'homepage/usage-insights'), { params: filter }),
  usageModes: (botId: string) =>
    Http.use().get<{ items: { id: string; label: string }[] }>(path(botId, 'homepage/usage-modes')),
  usage: (botId: string, filter: UsageFilter, cursor?: string) =>
    Http.use().get<Page<UsageRecord> & { to: string }>(path(botId, 'homepage/usage-records'), {
      params: { ...filter, cursor },
    }),
  usageDetail: (botId: string, id: string) =>
    Http.use().get<UsageDetail>(path(botId, `homepage/usage-records/${encodeURIComponent(id)}`)),
  generationModels: <T>(botId: string) => Http.use().get<{ items: T[] }>(path(botId, 'ai-generation-models')),
  get: (botId: string) => Http.use().get<Homepage>(path(botId, 'homepage')),
  preview: (botId: string) => Http.use().get<Bootstrap>(path(botId, 'homepage/preview')),
  templates: (before?: string, code?: string) =>
    Http.use().get<Page<HomepageTemplate>>('/homepage-templates', { params: { before, code } }),
  catalog: (after?: string) =>
    Http.use().get<Page<TemplateFamily>>('/homepage-template-catalog', { params: { after } }),
  act: (botId: string, value: PortalCommand, revision: number, reason: string, idempotencyKey: string) =>
    Http.use().put<Versioned<Binding>>(path(botId, 'homepage/actions'), {
      value,
      expected_revision: revision,
      reason,
      idempotency_key: idempotencyKey,
    }),
  templateCapabilities: () => Http.use().get<{ can_manage: boolean }>('/homepage-templates/capabilities'),
  upload: (name: string, code: string, file: File) => {
    const body = new FormData();
    body.set('name', name);
    body.set('code', code);
    body.set('package', file);
    return Http.use().post<HomepageTemplate>('/homepage-templates', body);
  },
  config: <T>(botId: string, name: string) => Http.use().get<Versioned<T>>(path(botId, name)),
  sourceShops: (botId: string, page: number) =>
    Http.use().get<unknown>(path(botId, 'homepage/sources/oimachi/shops'), { params: { page } }),
  save: <T>(botId: string, name: string, value: T, revision: number, reason: string, idempotencyKey: string) =>
    Http.use().put<Versioned<T>>(path(botId, name), {
      value,
      expected_revision: revision,
      reason,
      idempotency_key: idempotencyKey,
    }),
  data: (botId: string, cursor?: string) =>
    Http.use().get<Page<BusinessRecord>>(path(botId, 'business-data'), { params: { cursor } }),
  dataItem: (botId: string, type: string) =>
    Http.use().get<BusinessRecord>(path(botId, `homepage/business-data/${encodeURIComponent(type)}`)),
  putData: (botId: string, type: string, data: JsonValue, isPublic?: boolean) =>
    Http.use().put<BusinessRecord>(path(botId, `business-data/${encodeURIComponent(type)}`), {
      data,
      is_public: isPublic,
    }),
  publishContent: <T>(
    botId: string,
    command: {
      value: T;
      expected_revision: number;
      expected_allocation_revision: number;
      expected_draft_revision?: number;
      idempotency_key: string;
      reason: string;
    }
  ) => Http.use().put<Versioned<T>>(path(botId, 'homepage/content-release'), command),
  deleteData: (botId: string, type: string) =>
    Http.use().delete(path(botId, `business-data/${encodeURIComponent(type)}`)),
  events: (botId: string, cursor?: string, from?: string, to?: string) =>
    Http.use().get<Page<EventRecord> & { to: string }>(path(botId, 'events'), { params: { cursor, from, to } }),
  metrics: (botId: string, scope: string, cursor?: string) =>
    Http.use().get<Page<Metric> & { cleanup_complete: boolean; generated_at: string }>(
      path(botId, 'allocation-metrics'),
      { params: { fairness_scope: scope, cursor } }
    ),
  history: (botId: string, kind: string) =>
    Http.use().get<Page<History>>(path(botId, 'homepage/history'), { params: { kind } }),
};

export function homepageError(error: unknown): string {
  const value = error as { statusCode?: number; errorMsg?: string; detail?: { code?: string } };
  return value.detail?.code || value.errorMsg || (error instanceof Error ? error.message : String(error));
}
